import { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatDateDisplay } from '../utils/date';

interface DaySelectorProps {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  selectedDays: string[]; // Array of ISO date strings
  onChange: (selectedDays: string[]) => void;
  minSelections?: number;
  maxSelections?: number;
  scheduleCron?: string; // Cron expression for program schedule
  /**
   * Whether the program actually meets on a `YYYY-MM-DD` — its no-class dates, closure calendar
   * and `selective_dates`, which the cron alone cannot know. Pass `sessionDayPredicate(program)`:
   * a day a family can pick here is a day they are billed for (BHCS-133).
   *
   * A day already selected that fails it stays clickable, so an admin can take it back out.
   */
  isSelectableDate?: (ymd: string) => boolean;
}

export function DaySelector({ 
  startDate, 
  endDate, 
  selectedDays, 
  onChange, 
  minSelections = 1,
  maxSelections = undefined,
  scheduleCron,
  isSelectableDate
}: DaySelectorProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const selectedDaysSafe = Array.isArray(selectedDays) ? selectedDays : [];

  // Basic YYYY-MM-DD validator to avoid NaN dates and render a helpful prompt
  const isValidIsoDate = (value: string): boolean => {
    if (!value || typeof value !== 'string') return false;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return false;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
  };

  const hasValidRange = isValidIsoDate(startDate) && isValidIsoDate(endDate);

  // Parse start and end dates as local dates to match calendar day creation
  // This ensures consistent timezone handling without affecting day-of-week calculations
  const programStart = hasValidRange
    ? new Date(
        parseInt(startDate.split('-')[0]),
        parseInt(startDate.split('-')[1]) - 1,
        parseInt(startDate.split('-')[2])
      )
    : new Date();
  const programEnd = hasValidRange
    ? new Date(
        parseInt(endDate.split('-')[0]),
        parseInt(endDate.split('-')[1]) - 1,
        parseInt(endDate.split('-')[2]),
        23, 59, 59
      )
    : new Date();

  // Initialize current month to the program start month
  useEffect(() => {
    if (hasValidRange) {
      setCurrentMonth(new Date(programStart.getFullYear(), programStart.getMonth(), 1));
    }
  }, [startDate]);

  // Parse cron expression to get valid days of the week
  const getValidDaysOfWeek = (cron: string): number[] => {
    if (!cron) return [0, 1, 2, 3, 4, 5, 6]; // All days if no cron provided
    
    // Cron format: "minute hour day month day-of-week"
    // We need the last part (day-of-week)
    const parts = cron.trim().split(/\s+/);
    if (parts.length < 5) return [0, 1, 2, 3, 4, 5, 6]; // All days if invalid format
    
    const dayOfWeekPart = parts[4];
    
    // Handle different cron day-of-week formats
    if (dayOfWeekPart === '*') {
      return [0, 1, 2, 3, 4, 5, 6]; // All days
    }
    
    const validDays: number[] = [];
    
    // Split by comma for multiple days
    const dayParts = dayOfWeekPart.split(',');
    
    for (const part of dayParts) {
      if (part.includes('-')) {
        // Range format like "1-5"
        const [start, end] = part.split('-').map(Number);
        for (let i = start; i <= end; i++) {
          if (i >= 0 && i <= 6) {
            validDays.push(i);
          }
        }
      } else {
        // Single day
        const day = parseInt(part);
        if (!isNaN(day) && day >= 0 && day <= 6) {
          validDays.push(day);
        }
      }
    }
    
    return [...new Set(validDays)].sort(); // Remove duplicates and sort
  };

  const validDaysOfWeek = getValidDaysOfWeek(scheduleCron || '');

  // Get days in the current month view
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get first day of month and how many days in month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get first day of week (0 = Sunday)
    const firstDayOfWeek = firstDay.getDay();
    
    // Create array of days
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Create date in local timezone to match programStart/programEnd
      const date = new Date(year, month, day);
      // IMPORTANT: Build YYYY-MM-DD from local components to avoid UTC normalization
      // Using toISOString() would convert to UTC and can shift the date by +/-1 depending on TZ
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // Check if day is within program range
      const isInRange = date >= programStart && date <= programEnd;
      
      // Check if day matches program schedule
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const isScheduledDay = validDaysOfWeek.includes(dayOfWeek);
      
      // Check if day is selected
      const isSelected = selectedDaysSafe.includes(dateString);
      
      // Check if day is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPast = date < today;
      
      const isSelectable = isSelectableDate ? isSelectableDate(dateString) : true;

      days.push({
        date,
        dateString,
        day,
        isInRange,
        isScheduledDay,
        isSelected,
        isPast,
        isDisabled: !isInRange || isPast || !isScheduledDay || (!isSelectable && !isSelected)
      });
    }
    
    return days;
  };

  const handleDayClick = (dateString: string) => {
    if (selectedDaysSafe.includes(dateString)) {
      // Remove day if already selected (but check minimum)
      if (selectedDaysSafe.length > minSelections) {
        onChange(selectedDaysSafe.filter(d => d !== dateString));
      }
    } else {
      // Add day if not selected (but check maximum)
      if (!maxSelections || selectedDaysSafe.length < maxSelections) {
        onChange([...selectedDaysSafe, dateString].sort());
      }
    }
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const canGoPrev = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return prevMonth >= new Date(programStart.getFullYear(), programStart.getMonth(), 1);
  };

  const canGoNext = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return nextMonth <= new Date(programEnd.getFullYear(), programEnd.getMonth(), 1);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth();

  if (!hasValidRange) {
    return (
      <div className="bg-white border border-gray-100 rounded-md p-2 text-xs text-gray-600">
        Please set start date and end date
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-md p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={goToPrevMonth}
          disabled={!canGoPrev()}
          className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        
        <h3 className="text-sm font-medium text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          type="button"
          onClick={goToNextMonth}
          disabled={!canGoNext()}
          className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map((name, index) => (
          <div key={name} className={`text-center text-xs font-medium py-1 ${
            validDaysOfWeek.includes(index) ? 'text-blue-600' : 'text-gray-400'
          }`}>
            {name}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => (
          <div key={index} className="flex items-center justify-center py-[2px]">
            {day ? (
              <button
                type="button"
                onClick={() => !day.isDisabled && handleDayClick(day.dateString)}
                disabled={day.isDisabled}
                className={`
                  w-9 h-7 rounded text-xs font-medium transition-colors
                  ${day.isSelected 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : day.isDisabled
                    ? 'text-gray-300 cursor-not-allowed border border-transparent'
                    : day.isScheduledDay 
                    ? 'text-gray-900 hover:bg-blue-50 hover:text-blue-600 border border-blue-100'
                    : 'text-gray-400 cursor-not-allowed border border-transparent'
                  }
                  ${day.isInRange && !day.isPast && day.isScheduledDay ? '' : ''}
                `}
              >
                {day.day}
              </button>
            ) : (
              <div className="w-9 h-7" />
            )}
          </div>
        ))}
      </div>

      {/* Selection info */}
      <div className="mt-2 text-xs text-gray-600">
        <p>
          Selected: {selectedDaysSafe.length} day{selectedDaysSafe.length !== 1 ? 's' : ''}
          {minSelections > 1 && ` (minimum: ${minSelections})`}
          {maxSelections && ` (maximum: ${maxSelections})`}
        </p>
        {selectedDaysSafe.length > 0 && (
          <div className="mt-1">
            <p className="text-[11px] text-gray-500">Selected dates:</p>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {selectedDaysSafe.slice(0, 5).map(dateString => (
                <span
                  key={dateString}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-blue-100 text-blue-800"
                >
                  {formatDateDisplay(dateString)}
                </span>
              ))}
              {selectedDaysSafe.length > 5 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-gray-100 text-gray-600">
                  +{selectedDaysSafe.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}